import {
  LatLng,
  Region,
} from "@ghostgroup/wm-sdk-js/dist/types/modules/geo/types";
import WmlCookie from "./wml-cookies";
export interface WMWPRegion {
  latLng: LatLng;
  brandRegion: SimpleRegion;
  salesRegion: SimpleRegion;
  locationDetails?: any;
  createdAt: number;
}

export interface SimpleRegion {
  id: number;
  name: string;
  slug: string;
  region_path: string;
}

export class LocationWM {
  LOCATION_URL = "https://api-g.weedmaps.com/discovery/v1/location";
  TIMEOUT = 1000;
  STORAGE_KEY = "wm_wp_location";
  STORAGE_TTL = 259200000; // 3 Days

  latLng: LatLng;
  storedLocation: WMWPRegion;
  brandRegion: SimpleRegion;
  salesRegion: SimpleRegion;
  locationDetails?: any;

  async getLatLng(): Promise<LatLng | null> {
    if (this.latLng) {
      return this.latLng;
    }

    const cookie = WmlCookie.extractWmlCookie();

    if (cookie) {
      const { latitude, longitude } = cookie;
      return this.normalizeLatLng({ latitude, longitude });
    }

    return Promise.resolve(null);
  }

  async fetchLocation() {
    let url = new URL(this.LOCATION_URL);
    url.searchParams.set("include[]", "regions");
    if (this.latLng) {
      url.searchParams.set("latlng", Object.values(this.latLng).join(","));
    }
    return this.timeout(this.TIMEOUT, fetch(url.toString()))
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        this.salesRegion = this.normalizeSimpleRegion(
          response.data.regions.dispensary
        );
        this.brandRegion = this.normalizeSimpleRegion(
          response.data.regions.brand
        );
        this.locationDetails = response.data.location;

        if (!this.latLng) {
          const { latitude, longitude } = response.data.location;
          this.latLng = this.normalizeLatLng({ latitude, longitude });
        }

        return true;
      });
  }

  timeout(ms: number, promise: Promise<any>): Promise<any> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error("TIMEOUT"));
      }, ms);

      promise
        .then((value) => {
          clearTimeout(timer);
          resolve(value);
        })
        .catch((reason) => {
          clearTimeout(timer);
          reject(reason);
        });
    });
  }

  setStoredLocation(): void {
    const wm_wp_location: WMWPRegion = {
      latLng: this.latLng,
      brandRegion: this.brandRegion,
      salesRegion: this.salesRegion,
      createdAt: new Date().getTime(),
      locationDetails: this.locationDetails,
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(wm_wp_location));
  }

  normalizeSimpleRegion(region: Region): SimpleRegion {
    const region_path = region?.region_path || "";
    const name = region?.name || "";
    const id = region?.id || 0;
    const slug = region?.slug || "";
    return { id, name, slug, region_path };
  }

  normalizeLatLng(latLng: LatLng): LatLng {
    let latitude = parseFloat(latLng.latitude.toFixed(4));
    let longitude = parseFloat(latLng.longitude.toFixed(4));

    return { latitude, longitude };
  }

  async initialize() {
    return this.getLatLng()
      .then((latLng) => {
        if (latLng) {
          this.latLng = latLng;
        }
        // LatLng couldn't be determined, using stale LatLng for remainder of cache TTL if present
        else if (this.storedLocation?.latLng) {
          this.latLng = this.storedLocation.latLng;
        }

        if (
          this.storedLocation &&
          this.storedLocation.latLng?.latitude &&
          this.storedLocation.latLng?.longitude &&
          this.storedLocation.latLng?.latitude == this.latLng?.latitude &&
          this.storedLocation.latLng?.longitude == this.latLng?.longitude
        ) {
          this.brandRegion = this.storedLocation.brandRegion;
          this.salesRegion = this.storedLocation.salesRegion;
          this.locationDetails = this.storedLocation.locationDetails;
          return Promise.resolve(true);
        } else {
          return this.fetchLocation();
        }
      })
      .then(() => {
        if (this.brandRegion && this.salesRegion) {
          this.setStoredLocation();
        }
      });
  }

  constructor() {
    const regionStorage: WMWPRegion = JSON.parse(
      localStorage.getItem(this.STORAGE_KEY) as string
    );
    const ttlValue = Date.now() - this.STORAGE_TTL;
    if (regionStorage && regionStorage.createdAt > ttlValue) {
      this.storedLocation = regionStorage;
    }

    this.initialize();
  }
}
